import { useAccount, useConnect, useDisconnect, useConnectors, useChainId, useSwitchChain } from "wagmi";
import { base } from "wagmi/chains";
import { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";

function shortAddr(a: string) { return `${a.slice(0, 6)}…${a.slice(-4)}`; }

const DOGESHIT_INSTALL = "https://chromewebstore.google.com/detail/dogeshit-wallet/epbdphmhjbjiemaeabfkghhopangghli";

const BASE_CHAIN_PARAMS = {
  chainId: `0x${base.id.toString(16)}`,
  chainName: base.name,
  nativeCurrency: {
    name: base.nativeCurrency.name,
    symbol: base.nativeCurrency.symbol,
    decimals: base.nativeCurrency.decimals,
  },
  rpcUrls: ["https://mainnet.base.org", "https://base.drpc.org"],
  blockExplorerUrls: ["https://basescan.org"],
};

async function addBaseToWallet() {
  const ethereum = (window as any).ethereum;
  if (!ethereum) return false;
  try {
    await ethereum.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: BASE_CHAIN_PARAMS.chainId }],
    });
    return true;
  } catch (switchError: any) {
    if (switchError.code === 4902) {
      try {
        await ethereum.request({
          method: "wallet_addEthereumChain",
          params: [BASE_CHAIN_PARAMS],
        });
        return true;
      } catch {
        return false;
      }
    }
    return false;
  }
}

type WalletOption = {
  id: string;
  name: string;
  sub: string;
  icon: string;
  connector: any;
  isInstalled?: boolean;
  installUrl?: string;
};

/**
 * Wallet button — multi-wallet modal with fixed overlay.
 * Panel is above EVERYTHING (z-50 fixed overlay + portal).
 */
export function WalletButton() {
  const { address, isConnected } = useAccount();
  const { connect, isPending, error } = useConnect();
  const { disconnect } = useDisconnect();
  const rawChainId = useChainId();
  const chainId = typeof rawChainId === "string" ? parseInt(rawChainId, 10) : rawChainId;
  const { switchChain } = useSwitchChain();
  const allConnectors = useConnectors();
  const [open, setOpen] = useState(false);
  const [baseAdded, setBaseAdded] = useState(false);

  // Build wallet options from discovered connectors
  const wallets: WalletOption[] = useMemo(() => {
    const opts: WalletOption[] = [];

    // Injected / MetaMask / Rabby / DogeShit (EIP-6963)
    const injected = allConnectors.find((c) => c.type === "injected");
    const dogeshit = allConnectors.find((c) => /dogeshit|doge\s*shit/i.test(c.name));
    const metaMask = allConnectors.find((c) => /meta\s*mask|metamask/i.test(c.name));
    const rabby = allConnectors.find((c) => /rabby/i.test(c.name));

    if (dogeshit) {
      opts.push({
        id: "dogeshit",
        name: "DogeShit Wallet",
        sub: "EIP-7702 ready",
        icon: dogeshit.icon ?? "",
        connector: dogeshit,
        isInstalled: true,
      });
    } else {
      opts.push({
        id: "dogeshit",
        name: "DogeShit Wallet",
        sub: "not installed",
        icon: "",
        connector: null,
        isInstalled: false,
        installUrl: DOGESHIT_INSTALL,
      });
    }

    if (metaMask) {
      opts.push({
        id: "metamask",
        name: "MetaMask",
        sub: "popular",
        icon: metaMask.icon ?? "",
        connector: metaMask,
        isInstalled: true,
      });
    } else if (injected && !rabby) {
      opts.push({
        id: "injected",
        name: injected.name || "Browser Wallet",
        sub: "EIP-6963",
        icon: injected.icon ?? "",
        connector: injected,
        isInstalled: true,
      });
    }

    if (rabby) {
      opts.push({
        id: "rabby",
        name: "Rabby",
        sub: "smart wallet",
        icon: rabby.icon ?? "",
        connector: rabby,
        isInstalled: true,
      });
    }

    // Coinbase Wallet
    const coinbase = allConnectors.find((c) => c.type === "coinbaseWallet" || /coinbase/i.test(c.name));
    if (coinbase) {
      opts.push({
        id: "coinbase",
        name: "Coinbase Wallet",
        sub: "smart wallet",
        icon: coinbase.icon ?? "",
        connector: coinbase,
        isInstalled: true,
      });
    }

    // WalletConnect
    const wc = allConnectors.find((c) => c.type === "walletConnect");
    if (wc) {
      opts.push({
        id: "walletconnect",
        name: "WalletConnect",
        sub: "scan QR",
        icon: wc.icon ?? "",
        connector: wc,
        isInstalled: true,
      });
    }

    return opts;
  }, [allConnectors]);

  // Auto-switch / add Base on connect
  useEffect(() => {
    if (isConnected && chainId !== base.id) {
      if (switchChain) {
        switchChain({ chainId: base.id }, {
          onError: async () => {
            const ok = await addBaseToWallet();
            if (ok) setBaseAdded(true);
          },
        });
      } else {
        addBaseToWallet().then((ok) => {
          if (ok) setBaseAdded(true);
        });
      }
    }
  }, [isConnected, chainId, switchChain]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Lock body scroll when modal open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleConnect = useCallback((wallet: WalletOption) => {
    if (wallet.connector) {
      connect({ connector: wallet.connector });
      setOpen(false);
    }
  }, [connect]);

  if (isConnected && address) {
    return (
      <div className="flex items-center gap-3">
        <button
          onClick={() => disconnect()}
          className="group relative flex items-center gap-3 pl-1 pr-4 py-1.5 rounded-full bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] hover:border-white/[0.12] transition-all"
          title="Click to disconnect"
        >
          <div className="size-8 rounded-full bg-gradient-to-br from-teal-300 to-leaf grid place-items-center">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-ink-1000">
              <path d="M7 2 L7 7 L9.5 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="text-left">
            <div className="font-mono text-[11px] text-bone leading-none">{shortAddr(address)}</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-bone-dim mt-0.5">connected</div>
          </div>
          <div className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" className="text-bone-dim">
              <path d="M4 4 L10 10 M10 4 L4 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
          </div>
        </button>
      </div>
    );
  }

  const modalContent = (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-ink-1000/80 backdrop-blur-md" />

          {/* Modal card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[420px] card p-2 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.8)] overflow-hidden"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
              <div>
                <div className="font-display text-xl">connect wallet</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone-dim mt-1">
                  EIP-7702 · Base Network
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="size-8 rounded-xl hover:bg-white/10 flex items-center justify-center transition-colors"
                aria-label="Close"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M3 3 L11 11 M11 3 L3 11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            {/* Wallet list */}
            <div className="p-2 space-y-1 max-h-[60vh] overflow-y-auto">
              {wallets.map((wallet) => (
                <div key={wallet.id}>
                  {wallet.isInstalled ? (
                    <button
                      onClick={() => handleConnect(wallet)}
                      disabled={isPending}
                      className="flex w-full items-center gap-4 rounded-2xl px-4 py-3.5 text-left transition-all hover:bg-white/[0.06] focus:bg-white/[0.06] focus:outline-none group"
                    >
                      {wallet.icon ? (
                        <img
                          src={wallet.icon}
                          alt=""
                          className="size-11 rounded-xl bg-ink-900 object-contain p-0.5"
                        />
                      ) : (
                        <div className="size-11 rounded-xl bg-gradient-to-br from-sun to-coral grid place-items-center font-mono text-ink-1000 text-base font-bold">
                          {wallet.name.charAt(0)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-sm text-bone">{wallet.name}</div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone-dim mt-0.5">
                          {wallet.sub}
                        </div>
                      </div>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 14 14"
                        fill="none"
                        className="opacity-0 group-hover:opacity-40 transition-opacity"
                      >
                        <path d="M5 3 L9 7 L5 11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  ) : wallet.installUrl ? (
                    <a
                      href={wallet.installUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="flex w-full items-center gap-4 rounded-2xl px-4 py-3.5 text-left transition-all hover:bg-white/[0.06] group"
                    >
                      <div className="size-11 rounded-xl bg-gradient-to-br from-sun to-coral grid place-items-center font-mono text-ink-1000 text-base font-bold">
                        {wallet.name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-sm text-bone">{wallet.name}</div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone-dim mt-0.5">
                          not installed · click to install
                        </div>
                      </div>
                      <svg width="12" height="12" viewBox="0 0 14 14" fill="none" className="opacity-40">
                        <path d="M5 3 L9 3 L9 7 M9 3 L3 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                      </svg>
                    </a>
                  ) : null}
                </div>
              ))}

              {wallets.length === 0 && (
                <div className="px-4 py-8 text-center">
                  <div className="font-mono text-[12px] text-bone-dim mb-3">
                    No wallets detected.
                  </div>
                  <a
                    href="https://metamask.io/download/"
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-primary inline-flex text-sm"
                  >
                    install metamask
                  </a>
                </div>
              )}

              {error && (
                <div className="mt-2 px-4 py-3 rounded-xl bg-coral/10 border border-coral/20">
                  <div className="font-mono text-[11px] text-coral">
                    {error.message.split("\n")[0]}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-white/5 bg-white/[0.02]">
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-bone-dim text-center">
                {baseAdded ? "✓ Base network added" : "EIP-7702 enables AI-only mint"}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        disabled={isPending}
        className="btn btn-primary flex items-center gap-2"
      >
        {isPending ? (
          <>
            <span className="size-3.5 border-2 border-bone/30 border-t-bone rounded-full animate-spin" />
            connecting…
          </>
        ) : (
          <>
            connect wallet
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M3 8 H13 M9 4 L13 8 L9 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </>
        )}
      </button>
      {createPortal(modalContent, document.body)}
    </>
  );
}
